import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    getSessionGroupOptions,
    updateSessionGroupMutation,
    orderSessionGroupTemplatesMutation,
    publishSessionGroup,
    unpublishSessionGroup,
} from '@/api';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'react-hot-toast';

export const Route = createFileRoute('/_auth/session-groups/$id')({
    component: Page,
});

function Page() {
    const { id } = Route.useParams();
    const group = useQuery({ ...getSessionGroupOptions({ path: { group_id: id } }), refetchOnWindowFocus: false });

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isPublishingToggle, setIsPublishingToggle] = useState(false);

    const update = useMutation({ ...updateSessionGroupMutation(), onSuccess: () => group.refetch() });
    const reorder = useMutation({ ...orderSessionGroupTemplatesMutation(), onSuccess: () => group.refetch() });

    const onSave = () => {
        update.mutate({ path: { group_id: id }, body: { name: name || undefined, description: description || undefined } });
    };

    const onReorder = () => {
        const current = (group.data as any)?.session_template_ids || [];
        // For now, keep as-is; placeholder for drag-and-drop reorder
        reorder.mutate({ path: { group_id: id }, body: { session_template_ids: current } });
    };

    const handleTogglePublish = async () => {
        setIsPublishingToggle(true);
        try {
            const g: any = group.data;
            if (g.is_published) {
                await unpublishSessionGroup({ path: { group_id: id } });
                toast.success('Unpublished');
            } else {
                await publishSessionGroup({ path: { group_id: id } });
                toast.success('Published');
            }
            group.refetch();
        } catch {
            toast.error('Failed to update publish status');
        } finally {
            setIsPublishingToggle(false);
        }
    };

    if (group.isLoading) return <div className="p-4 text-sm text-muted-foreground">Loading...</div>;
    if (!group.data) return <div className="p-4 text-sm text-muted-foreground">Not found</div>;

    const g: any = group.data;

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="text-xs text-muted-foreground">Group ID: {g._id}</div>
                    {g.is_published && <Badge variant="default">Published</Badge>}
                </div>
                <Button
                    variant={g.is_published ? 'outline' : 'default'}
                    size="sm"
                    onClick={handleTogglePublish}
                    disabled={isPublishingToggle}
                >
                    {g.is_published ? 'Unpublish' : 'Publish'}
                </Button>
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSave();
                }}
                className="space-y-2">
                <Input placeholder="Name" defaultValue={g.name} onChange={(e) => setName(e.target.value)} />
                <Input placeholder="Description" defaultValue={g.description} onChange={(e) => setDescription(e.target.value)} />
                <div className="flex gap-2">
                    <Button type="submit" disabled={update.isPending}>Save</Button>
                    <Button type="button" variant="outline" onClick={onReorder} disabled={reorder.isPending}>Reorder (placeholder)</Button>
                </div>
            </form>

            <div className="space-y-2">
                <div className="text-sm font-medium">Sessions in Group</div>
                <div className="text-xs text-muted-foreground">{(g.session_template_ids || []).length} sessions</div>
            </div>
        </div>
    );
}
